import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  GripVertical,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings as SettingsIcon,
  StickyNote,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { apiService } from '@/services/api'
import { cn, formatNumberSmart } from '@/lib/utils'

const NUTRITION_PROGRAM_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'categories', label: 'Categories', icon: SettingsIcon },
]

const EMPTY_PAGINATION = { pageIndex: 0, pageSize: 25 }
const EMPTY_SEARCH_PAGINATION = { pageIndex: 0, pageSize: 10 }

const DAY_ORDER = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa']
const DAY_LABELS = { su: 'Su', mo: 'Mo', tu: 'Tu', we: 'We', th: 'Th', fr: 'Fr', sa: 'Sa' }
const DAY_FIELD_MAP = { su: 'sunday', mo: 'monday', tu: 'tuesday', we: 'wednesday', th: 'thursday', fr: 'friday', sa: 'saturday' }
const MEAL_NAME_OPTIONS = ['Breakfast', 'Snack', 'Lunch', 'Dinner']
const DEFAULT_MEAL_SLOTS = [
  { name: 'Breakfast', time: '09:00' },
  { name: 'Snack', time: '12:00' },
  { name: 'Lunch', time: '14:00' },
  { name: 'Snack', time: '17:00' },
  { name: 'Dinner', time: '20:00' },
]

const EmptyState = ({ message }) => (
  <div className="uploader-empty">
    {message}
  </div>
)

const PageHero = ({ title, description, onBack }) => (
  <div className="app-page-hero">
    <div className="absolute right-0 top-0 h-36 w-36 rounded-full hidden" />
    <div className="app-page-hero-content space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        ) : null}
      </div>
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="max-w-3xl text-base leading-7">{description}</p>
      </div>
    </div>
  </div>
)

const WorkspaceTabs = ({ tabs, activeTab, onChange }) => (
  <div className="uploader-tabs">
    {tabs.map((tab) => {
      const Icon = tab.icon
      const isActive = activeTab === tab.id
      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={cn(
            '-mb-px inline-flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition-colors',
            isActive ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'
          )}
        >
          <Icon className="h-4 w-4" />
          {tab.label}
        </button>
      )
    })}
  </div>
)

const PaginationFooter = ({ pageIndex, pageSize, totalCount, onPageChange }) => {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const from = totalCount ? pageIndex * pageSize + 1 : 0
  const to = Math.min(totalCount, pageIndex * pageSize + pageSize)

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <p>
        Showing <span className="font-medium text-slate-900">{from}-{to}</span> of{' '}
        <span className="font-medium text-slate-900">{totalCount}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={() => onPageChange(Math.max(0, pageIndex - 1))} disabled={pageIndex === 0}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[88px] text-center">
          Page {Math.min(pageIndex + 1, pageCount)} of {pageCount}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
          disabled={pageIndex >= pageCount - 1}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const createDraftId = (prefix = 'draft') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const parseNumberField = (value) => {
  if (value === '' || value === null || value === undefined) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNumberOrZero = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const tryParseJSON = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (error) {
    return value
  }
}

const formatTimeForInput = (value) => {
  if (!value) return ''
  if (typeof value === 'string' && value.includes(':')) return value.slice(0, 5)
  return value
}

const normalizeTimePayload = (value) => {
  if (!value) return ''
  const trimmed = String(value).trim()
  if (!trimmed) return ''
  return trimmed.length === 5 ? `${trimmed}:00` : trimmed
}

const normalizeNutritionCalories = (item) => {
  const directCalories = item?.calories
  if (directCalories !== '' && directCalories !== null && directCalories !== undefined) return parseNumberField(directCalories)
  const nutrientValue = item?.nutrient_value
  if (nutrientValue !== '' && nutrientValue !== null && nutrientValue !== undefined) return parseNumberField(nutrientValue)
  return 0
}

const buildNutritionSearchDraft = (item) => ({
  id: item?.resource_id || item?.nix_item_id || createDraftId('nutrition-result'),
  name: item?.item_name || item?.name || 'Food',
  brandName: item?.brand_name || '',
  unit: item?.serving_uom || item?.serving_unit || '',
  servingQty: parseNumberField(item?.serving_qty),
  calories: normalizeNutritionCalories(item),
  thumbnail: item?.thumbnail || item?.photo?.thumb || '',
  raw: item,
})

const buildNutritionDetailMap = (detail) => {
  const fields = [
    ['brand_name', 'Brand'],
    ['calories', 'Calories'],
    ['protein', 'Protein'],
    ['carbs', 'Carbs'],
    ['fat', 'Fat'],
    ['fiber', 'Fiber'],
    ['sugars', 'Sugars'],
    ['serving_qty', 'Serving Qty'],
    ['serving_unit', 'Serving Unit'],
    ['serving_weight_grams', 'Serving Weight (g)'],
    ['cholesterol', 'Cholesterol'],
    ['sodium', 'Sodium'],
    ['potassium', 'Potassium'],
    ['calcium', 'Calcium'],
    ['iron', 'Iron'],
    ['vitamin_c', 'Vitamin C'],
  ]

  return fields
    .map(([key, label]) => ({ key, label, value: detail?.[key] }))
    .filter((field) => field.value !== null && field.value !== undefined && field.value !== '')
}

const getNutritionMeasureOptions = (detail) => {
  const altMeasures = Array.isArray(detail?.alt_measures) ? detail.alt_measures : []
  const primaryMeasure = {
    id: `${detail?.food_name || detail?.name || 'measure'}-primary`,
    qty: parseNumberField(detail?.serving_qty || detail?.qty || 1),
    measure: detail?.serving_unit || detail?.unit || 'serving',
    serving_weight: parseNumberField(detail?.serving_weight_grams),
    calories: parseNumberField(detail?.calories ?? detail?.nf_calories),
    protein: parseNumberField(detail?.protein ?? detail?.nf_protein),
    carbs: parseNumberField(detail?.carbs ?? detail?.nf_total_carbohydrate),
    fat: parseNumberField(detail?.fat ?? detail?.nf_total_fat),
  }

  const mappedAltMeasures = altMeasures.map((measure, index) => ({
    id: `${detail?.food_name || detail?.name || 'measure'}-${measure?.seq ?? index}`,
    qty: parseNumberField(measure?.qty || 1),
    measure: measure?.measure || detail?.serving_unit || 'serving',
    serving_weight: parseNumberField(measure?.serving_weight),
    calories: parseNumberField(measure?.calories),
    protein: parseNumberField(measure?.protein),
    carbs: parseNumberField(measure?.carbs),
    fat: parseNumberField(measure?.fat),
  }))

  return [primaryMeasure, ...mappedAltMeasures].filter(
    (measure, index, arr) =>
      arr.findIndex((candidate) => `${candidate.qty}-${candidate.measure}`.toLowerCase() === `${measure.qty}-${measure.measure}`.toLowerCase()) === index
  )
}

const buildProgramFoodDraft = (item, sourceType = 'library', overrides = {}) => {
  const parsedBaseQty = parseNumberField(overrides.baseQty ?? item?.qty ?? item?.serving_qty ?? item?.servingQty ?? 1)
  const baseQty = parsedBaseQty > 0 ? parsedBaseQty : 1
  return {
    id: overrides.id || createDraftId(sourceType),
    persistedId: overrides.persistedId ?? item?.id ?? null,
    sourceId: overrides.sourceId ?? item?.resource_id ?? item?.nix_item_id ?? item?.sourceId ?? item?.id ?? null,
    sourceType,
    name: overrides.name || item?.name || item?.food_name || item?.item_name || 'Food',
    unit: overrides.unit || item?.unit || item?.serving_unit || item?.serving_uom || '',
    qty: overrides.qty ?? baseQty,
    baseQty,
    baseCalories: parseNumberField(overrides.baseCalories ?? item?.calories ?? item?.nf_calories),
    baseProtein: parseNumberField(overrides.baseProtein ?? item?.protein ?? item?.nf_protein),
    baseCarbs: parseNumberField(overrides.baseCarbs ?? item?.carbs ?? item?.nf_total_carbohydrate),
    baseFat: parseNumberField(overrides.baseFat ?? item?.fat ?? item?.nf_total_fat),
    note: overrides.note ?? item?.note ?? '',
    detail: overrides.detail || item || null,
  }
}

const buildProgramFoodFromNutritionMeasure = (detail, measure) =>
  buildProgramFoodDraft(detail, 'nutrition', {
    name: detail?.food_name || detail?.name || 'Food',
    unit: measure?.measure || detail?.serving_unit || detail?.unit || '',
    qty: measure?.qty || 1,
    baseQty: measure?.qty || 1,
    baseCalories: measure?.calories ?? detail?.calories ?? detail?.nf_calories,
    baseProtein: measure?.protein ?? detail?.protein ?? detail?.nf_protein,
    baseCarbs: measure?.carbs ?? detail?.carbs ?? detail?.nf_total_carbohydrate,
    baseFat: measure?.fat ?? detail?.fat ?? detail?.nf_total_fat,
    detail,
  })

const getFoodScale = (food) => {
  const qty = Math.max(parseNumberField(food?.qty), 0)
  const baseQty = Math.max(parseNumberField(food?.baseQty), 0)
  if (qty === 0) return 0
  if (baseQty > 0) return qty / baseQty
  return qty
}

const getFoodMacros = (food) => {
  const scale = getFoodScale(food)
  return {
    calories: parseNumberField(food?.baseCalories) * scale,
    protein: parseNumberField(food?.baseProtein) * scale,
    carbs: parseNumberField(food?.baseCarbs) * scale,
    fat: parseNumberField(food?.baseFat) * scale,
  }
}

const calculatePlanTotals = (plan) =>
  (plan?.meals || []).reduce(
    (totals, meal) => {
      ;(meal.items || []).forEach((item) => {
        const macros = getFoodMacros(item)
        totals.calories += macros.calories
        totals.protein += macros.protein
        totals.carbs += macros.carbs
        totals.fat += macros.fat
      })
      return totals
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  )

const createMealDraft = (seed = {}) => ({
  id: seed.id || createDraftId('meal'),
  persistedId: seed.persistedId ?? null,
  name: seed.name || 'Meal',
  time: formatTimeForInput(seed.time || '09:00'),
  duration: seed.duration ?? 0,
  waters: seed.waters ?? 0,
  waternote: seed.waternote || '',
  items: seed.items || [],
})

const createProgramPlanDraft = (name) => ({
  id: createDraftId('plan'),
  persistedId: null,
  name,
  days: [],
  meals: DEFAULT_MEAL_SLOTS.map((slot) => createMealDraft({ name: slot.name, time: slot.time })),
})

const buildProgramFoodPayload = (food) => {
  const macros = getFoodMacros(food)
  return {
    ...(food?.detail && typeof food.detail === 'object' ? food.detail : {}),
    id: food?.persistedId || food?.sourceId || undefined,
    name: food?.name || 'Food',
    qty: Math.max(parseNumberField(food?.qty), 0),
    quantity: Math.max(parseNumberField(food?.qty), 0),
    unit: food?.unit || '',
    calories: macros.calories,
    protein: macros.protein,
    carbs: macros.carbs,
    fat: macros.fat,
    note: food?.note || '',
  }
}

const buildNutritionProgramPayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  category: form.categoryId ? Number(form.categoryId) : form.newCategoryName.trim(),
  subcat: form.newCategoryParentId ? Number(form.newCategoryParentId) : -1,
  mealplans: (form.mealplans || []).map((plan) => ({
    id: plan.persistedId || undefined,
    name: plan.name.trim(),
    weeks: DAY_ORDER.map((day) => ({
      key: DAY_FIELD_MAP[day],
      value: DAY_LABELS[day],
      ...(plan.days.includes(day) ? { checked: true } : {}),
    })),
    isAllWeeksSelected: plan.days.length === DAY_ORDER.length,
    days: plan.days.join(','),
    meals: (plan.meals || []).map((meal) => ({
      id: meal.persistedId || undefined,
      name: meal.name.trim(),
      mealtime: normalizeTimePayload(meal.time),
      duration: parseNumberField(meal.duration),
      waters: parseNumberField(meal.waters),
      waternote: meal.waternote || '',
      mealfoods: (meal.items || []).map(buildProgramFoodPayload),
    })),
  })),
})

const normalizeTextField = (value) => (value === null || value === undefined ? '' : String(value).trim())
const normalizeOptionalValue = (value) => (value === null || value === undefined || `${value}`.trim() === '' ? '' : String(value).trim())
const areEqualByJson = (left, right) => JSON.stringify(left) === JSON.stringify(right)

const buildNutritionProgramComparable = (form = {}) => ({
  name: normalizeTextField(form.name),
  description: normalizeTextField(form.description),
  categoryId: normalizeOptionalValue(form.categoryId),
  subcat: normalizeOptionalValue(form.newCategoryParentId),
})

const buildNutritionMealComparable = (meal = {}) => ({
  name: normalizeTextField(meal.name),
  time: normalizeTimePayload(meal.time),
  duration: normalizeOptionalValue(meal.duration),
  waters: normalizeOptionalValue(meal.waters),
  waternote: normalizeTextField(meal.waternote),
  itemKeys: (meal.items || []).map((item) => item.persistedId || item.id),
})

const buildNutritionMealPlanComparable = (plan = {}) => ({
  name: normalizeTextField(plan.name),
  days: DAY_ORDER.filter((day) => plan.days?.includes(day)),
  meals: (plan.meals || []).map(buildNutritionMealComparable),
})

const buildNutritionMealFoodComparable = (item = {}) => {
  const macros = getFoodMacros(item)
  return {
    name: normalizeTextField(item.name),
    qty: `${Math.max(parseNumberField(item.qty), 0)}`,
    unit: normalizeTextField(item.unit),
    calories: `${macros.calories}`,
    protein: `${macros.protein}`,
    carbs: `${macros.carbs}`,
    fat: `${macros.fat}`,
    note: item.note || '',
  }
}

const buildNutritionProgramUpdatePayload = (form = {}) => ({
  name: normalizeTextField(form.name),
  description: normalizeTextField(form.description),
  category: form.categoryId ? Number(form.categoryId) : normalizeTextField(form.newCategoryName),
  subcat: form.newCategoryParentId ? Number(form.newCategoryParentId) : -1,
})

const buildNutritionMealPlanPayload = (plan = {}) => ({
  id: plan.persistedId || undefined,
  name: normalizeTextField(plan.name),
  ...DAY_ORDER.reduce((acc, day) => {
    acc[DAY_FIELD_MAP[day]] = plan.days?.includes(day) ? 1 : 0
    return acc
  }, {}),
  weeks: DAY_ORDER.map((day) => ({
    key: DAY_FIELD_MAP[day],
    value: DAY_LABELS[day],
    ...(plan.days?.includes(day) ? { checked: true } : {}),
  })),
  isAllWeeksSelected: (plan.days || []).length === DAY_ORDER.length,
  days: (plan.days || []).join(','),
  meals: (plan.meals || []).map((meal) => ({
    id: meal.persistedId || undefined,
    name: normalizeTextField(meal.name),
    mealtime: normalizeTimePayload(meal.time),
    duration: parseNumberField(meal.duration),
    waters: parseNumberField(meal.waters),
    waternote: meal.waternote || '',
    mealfoods: (meal.items || []).map(buildProgramFoodPayload),
  })),
})

const buildNutritionMealFoodUpdatePayload = (item = {}) => {
  const macros = getFoodMacros(item)
  return {
    name: normalizeTextField(item.name),
    qty: `${Math.max(parseNumberField(item.qty), 0)}`,
    unit: item.unit || '',
    calories: macros.calories,
    protein: macros.protein,
    carbs: macros.carbs,
    fat: macros.fat,
    note: item.note || '',
  }
}

const mapTreeNode = (node, index, parentKey = 'root', rootSection = '', depth = 0) => {
  const childrenSource = tryParseJSON(
    node?.children || node?.child || node?.nodes || node?.items || node?.subItems || node?.programnutritiontabtreeindex || []
  )
  const nodeName = node?.name || node?.title || node?.label || 'Item'
  const nextRootSection = depth === 0 ? nodeName : rootSection
  const rawId = node?.id || node?.guid || node?.value || node?.name || `node-${index}`
  const stableId = `${parentKey}-${String(rawId)}-${index}`
  const children = Array.isArray(childrenSource)
    ? childrenSource.map((child, childIndex) => mapTreeNode(child, childIndex, stableId, nextRootSection, depth + 1))
    : []

  return {
    id: stableId,
    sourceId: node?.id || node?.guid || node?.value || null,
    name: nodeName,
    category: node?.category || node?.catname || node?.cat_name || node?.parent_name || '',
    calories: toNumberOrZero(node?.calories || node?.kcal),
    protein: toNumberOrZero(node?.protein || node?.proteins),
    carbs: toNumberOrZero(node?.carbs || node?.carbohydrates),
    fat: toNumberOrZero(node?.fat || node?.fats),
    qty: node?.qty ?? node?.quantity ?? 1,
    unit: node?.unit || node?.serving || node?.serving_unit || '',
    rootSection: nextRootSection,
    depth,
    children,
  }
}

const normalizeNutritionProgramTree = (raw) => {
  const sourceRoot = Array.isArray(raw) ? raw : []
  const nested = sourceRoot[0]?.programnutritiontabtreeindex || sourceRoot
  const parsed = tryParseJSON(nested)
  const nodes = Array.isArray(parsed) ? parsed.map((node, index) => mapTreeNode(node, index)) : []
  if (nodes.length === 1 && nodes[0].name?.toLowerCase() === 'nutrition program templates' && nodes[0].children?.length) {
    return nodes[0].children
  }
  return nodes
}

const countLeaves = (node) => {
  if (!node?.children?.length) return 1
  return node.children.reduce((total, child) => total + countLeaves(child), 0)
}

const buildLibraryExpansionState = (nodes, value) =>
  (nodes || []).reduce((acc, node) => {
    acc[node.id] = value
    if (node.children?.length) Object.assign(acc, buildLibraryExpansionState(node.children, value))
    return acc
  }, {})

const filterLibraryTree = (nodes, term) => {
  const normalizedTerm = String(term || '').trim().toLowerCase()
  if (!normalizedTerm) return nodes

  return (nodes || [])
    .map((node) => {
      const children = filterLibraryTree(node.children || [], normalizedTerm)
      const matchesSelf =
        String(node.name || '').toLowerCase().includes(normalizedTerm) ||
        String(node.category || '').toLowerCase().includes(normalizedTerm)

      if (matchesSelf || children.length) return { ...node, children }
      return null
    })
    .filter(Boolean)
}

const flattenLibraryNode = (node) => {
  if (!node?.children?.length) return [node]
  return node.children.flatMap(flattenLibraryNode)
}

const classifyLibraryDropNode = (node) => {
  const section = String(node?.rootSection || '').trim().toLowerCase()
  if (section === 'nutrition program templates') return 'program-plan'
  if (section === 'meals') return 'meal-template'
  return 'single-food'
}

const truthyDay = (value) => value === 1 || value === '1' || value === true
const firstArray = (...values) => values.find((value) => Array.isArray(value)) || []

const normalizeProgramDetail = (raw, fallbackRow = null) => {
  const data = raw?.data || raw || {}
  const mealplansRaw = firstArray(data?.nutritionprogrammealplans, data?.mealplans, data?.rows, fallbackRow?.nutritionprogrammealplans)

  return {
    id: data?.id || fallbackRow?.id || null,
    name: data?.name || fallbackRow?.name || '',
    description: data?.description || fallbackRow?.description || '',
    categoryId: `${data?.nutritionprogramcategory_id ?? fallbackRow?.nutritionprogramcategory_id ?? ''}`,
    newCategoryName: '',
    newCategoryParentId: '',
    mealplans: mealplansRaw.map((plan) => {
      const mealsRaw = firstArray(plan?.nutritionprogrammeals, plan?.meals)
      return {
        id: createDraftId('edit-plan'),
        persistedId: plan?.id || null,
        name: plan?.name || 'Meal Plan',
        days: DAY_ORDER.filter((day) => truthyDay(plan?.[DAY_FIELD_MAP[day]])),
        meals: mealsRaw.map((meal) =>
          createMealDraft({
            id: createDraftId('edit-meal'),
            persistedId: meal?.id || null,
            name: meal?.name || 'Meal',
            time: meal?.mealtime || meal?.time || '',
            duration: meal?.duration ?? 0,
            waters: meal?.waters ?? meal?.water_8oz ?? 0,
            waternote: meal?.waternote || '',
            items: firstArray(meal?.nutritionprogrammealfoods, meal?.mealfoods, meal?.foods).map((item) =>
              buildProgramFoodDraft(item, 'persisted', {
                id: createDraftId('edit-food'),
                persistedId: item?.id || null,
                name: item?.name || item?.food_name || 'Food',
                unit: item?.unit || item?.serving_unit || '',
                qty: item?.qty ?? item?.quantity ?? 1,
                baseQty: item?.qty ?? item?.quantity ?? 1,
                baseCalories: item?.calories,
                baseProtein: item?.protein,
                baseCarbs: item?.carbs,
                baseFat: item?.fat,
                note: item?.note || '',
                detail: item,
              })
            ),
          })
        ),
      }
    }),
  }
}

const DropZone = ({ mealId, planId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `nutrition-meal-drop-${mealId}`, data: { type: 'mealDrop', mealId, planId } })
  return <div ref={setNodeRef} className={isOver ? 'rounded-lg ring-2 ring-fitness-primary/40' : ''}>{children}</div>
}

const ProgramDropZone = ({ mode, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `nutrition-plan-canvas-${mode}`, data: { type: 'planCanvas', mode } })
  return (
    <div ref={setNodeRef} className={cn('rounded-lg transition', isOver && 'ring-2 ring-fitness-primary/40')}>
      {children}
    </div>
  )
}

const LibraryGroup = ({ node, expanded, onToggle, childrenCount, children }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-3 text-left">
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="text-sm font-semibold text-slate-800">{node.name}</span>
      </div>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{childrenCount}</span>
    </button>
    {expanded ? <div className="space-y-2 pl-4">{children}</div> : null}
  </div>
)

const LibraryLeaf = ({ node }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
    id: node.id,
    data: { type: 'libraryItem', node },
  })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group flex cursor-grab items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm shadow-sm transition active:cursor-grabbing',
        isDragging ? 'border-slate-200 bg-white/40 opacity-0' : 'border-slate-200 bg-white hover:border-fitness-primary/40'
      )}
    >
      <div className="min-w-0">
        <p className="break-words text-sm font-semibold text-slate-800">{node.name}</p>
        <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
          <span className="rounded-full bg-slate-100 px-2 py-0.5">{node.calories ? `${formatNumberSmart(node.calories)} kcal` : '--'}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">P {formatNumberSmart(node.protein || 0)}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">C {formatNumberSmart(node.carbs || 0)}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">F {formatNumberSmart(node.fat || 0)}</span>
        </div>
      </div>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400 transition group-hover:border-fitness-primary/40 group-hover:text-fitness-primary">
        <GripVertical className="h-4 w-4" />
      </div>
    </div>
  )
}

const LibraryDragOverlay = ({ node }) => (
  <div className="pointer-events-none flex items-center justify-between gap-4 rounded-lg border border-fitness-primary/40 bg-white px-4 py-3 text-sm shadow-sm ring-2 ring-fitness-primary/20">
    <div className="min-w-0">
      <p className="break-words text-sm font-semibold text-slate-800">{node?.name}</p>
      <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
        <span className="rounded-full bg-slate-100 px-2 py-0.5">{node?.calories ? `${formatNumberSmart(node.calories)} kcal` : '--'}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5">P {formatNumberSmart(node?.protein || 0)}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5">C {formatNumberSmart(node?.carbs || 0)}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5">F {formatNumberSmart(node?.fat || 0)}</span>
      </div>
    </div>
    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary">
      <GripVertical className="h-4 w-4" />
    </div>
  </div>
)

const SortableMealItem = ({
  item,
  mealId,
  planId,
  onFieldChange,
  onQtyChange,
  onCopy,
  onDelete,
  onNote,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { type: 'mealItem', mealId, planId },
  })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const macros = getFoodMacros(item)
  const hasNote = Boolean(item.note && String(item.note).trim())

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'grid grid-cols-[auto,2.2fr,0.7fr,0.9fr,0.7fr,0.7fr,0.7fr,0.7fr,auto] items-center gap-2 rounded-xl px-2 py-2',
        isDragging && 'bg-white shadow-sm ring-1 ring-fitness-primary/30'
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex h-9 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-400 transition hover:border-slate-300 hover:text-slate-700"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Input value={item.name || ''} onChange={(event) => onFieldChange('name', event.target.value)} className="h-10" />
      <Input type="number" min="0" step="any" value={item.qty} onChange={(event) => onQtyChange(event.target.value)} className="h-10" />
      <Input readOnly value={item.unit || ''} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.calories)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.protein)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.carbs)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.fat)} className="h-10 bg-slate-50" />
      <div className="flex items-center justify-end gap-1">
        <button type="button" onClick={onCopy} className="rounded-md border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800" title="Copy row">
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onNote}
          className={cn(
            'rounded-md border p-2 transition hover:border-slate-300 hover:text-slate-800',
            hasNote ? 'border-fitness-primary/40 text-fitness-primary' : 'border-slate-200 text-slate-500'
          )}
          title={hasNote ? 'Edit note' : 'Add note'}
        >
          <StickyNote className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={onDelete} className="rounded-md border border-red-200 p-2 text-red-500 transition hover:border-red-300 hover:text-red-600" title="Delete row">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

const StaticMealItem = ({
  item,
  onFieldChange,
  onQtyChange,
  onCopy,
  onDelete,
  onNote,
}) => {
  const macros = getFoodMacros(item)
  const hasNote = Boolean(item.note && String(item.note).trim())

  return (
    <div className="grid grid-cols-[auto,2.2fr,0.7fr,0.9fr,0.7fr,0.7fr,0.7fr,0.7fr,auto] items-center gap-2 rounded-xl px-2 py-2">
      <span className="block h-9 w-7" />
      <Input value={item.name || ''} onChange={(event) => onFieldChange('name', event.target.value)} className="h-10" />
      <Input type="number" min="0" step="any" value={item.qty} onChange={(event) => onQtyChange(event.target.value)} className="h-10" />
      <Input readOnly value={item.unit || ''} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.calories)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.protein)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.carbs)} className="h-10 bg-slate-50" />
      <Input readOnly value={formatNumberSmart(macros.fat)} className="h-10 bg-slate-50" />
      <div className="flex items-center justify-end gap-1">
        <button type="button" onClick={onCopy} className="rounded-md border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800" title="Copy row">
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onNote}
          className={cn(
            'rounded-md border p-2 transition hover:border-slate-300 hover:text-slate-800',
            hasNote ? 'border-fitness-primary/40 text-fitness-primary' : 'border-slate-200 text-slate-500'
          )}
          title={hasNote ? 'Edit note' : 'Add note'}
        >
          <StickyNote className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={onDelete} className="rounded-md border border-red-200 p-2 text-red-500 transition hover:border-red-300 hover:text-red-600" title="Delete row">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

const NutritionSearchPanel = ({
  targetMealOptions,
  targetMealId,
  onTargetMealChange,
  searchTerm,
  onSearchTermChange,
  loading,
  rows,
  totalCount,
  pageIndex,
  pageSize,
  onPageChange,
  expandedRowId,
  onToggleRow,
  detailMap,
  detailLoadingId,
  onAddNutritionFood,
}) => (
  <Card className="border-0 shadow-sm ring-1 ring-slate-200/80">
    <CardHeader>
      <CardTitle className="text-base font-semibold text-slate-900">Nutritionix Search</CardTitle>
      <CardDescription>Add a new food item into the selected meal.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Target Meal</label>
        <select
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
          value={targetMealId || ''}
          onChange={(event) => onTargetMealChange(event.target.value)}
        >
          <option value="">Select meal</option>
          {targetMealOptions.map((meal) => (
            <option key={meal.id} value={meal.id}>
              {meal.label}
            </option>
          ))}
        </select>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={searchTerm} onChange={(event) => onSearchTermChange(event.target.value)} placeholder="Search Nutritionix foods" className="pl-9" />
      </div>

      {!targetMealId ? (
        <EmptyState message="Pick a meal first, then search Nutritionix foods." />
      ) : loading ? (
        <EmptyState message="Searching foods..." />
      ) : searchTerm.trim() ? (
        rows.length ? (
          <>
            <div className="space-y-3">
              {rows.map((row) => {
                const detail = detailMap[row.id]
                const expanded = expandedRowId === row.id
                return (
                  <div key={row.id} className="rounded-lg border border-slate-200 bg-white">
                    <button type="button" onClick={() => onToggleRow(row)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {row.brandName || 'Common food'} • {formatNumberSmart(row.servingQty)} {row.unit || 'serving'} • {formatNumberSmart(row.calories)} kcal
                        </p>
                      </div>
                      {detailLoadingId === row.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </button>
                    {expanded && detail ? (
                      <div className="space-y-3 border-t border-slate-100 px-4 py-4">
                        {getNutritionMeasureOptions(detail).map((measure) => (
                          <div key={`${row.id}-${measure.id}`} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-slate-900">
                                {detail.food_name || row.name} - {formatNumberSmart(measure.qty)} {measure.measure}
                              </p>
                              <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                                <span>Calories: {formatNumberSmart(measure.calories)}</span>
                                <span>Protein: {formatNumberSmart(measure.protein)}</span>
                                <span>Carbs: {formatNumberSmart(measure.carbs)}</span>
                                <span>Fat: {formatNumberSmart(measure.fat)}</span>
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <Button type="button" variant="outline" onClick={() => onAddNutritionFood(detail, measure)}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add {measure.measure}
                              </Button>
                            </div>
                          </div>
                        ))}
                        <div className="grid gap-3 sm:grid-cols-2">
                          {buildNutritionDetailMap(detail).map((field) => (
                            <div key={`${row.id}-${field.key}`} className="rounded-xl border border-slate-200 px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">{field.label}</p>
                              <p className="mt-1 text-sm font-medium text-slate-900">{field.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
            <PaginationFooter pageIndex={pageIndex} pageSize={pageSize} totalCount={totalCount} onPageChange={onPageChange} />
          </>
        ) : (
          <EmptyState message="No foods matched the current search." />
        )
      ) : (
        <p className="text-sm text-slate-500">Start typing to search Nutritionix foods.</p>
      )}
    </CardContent>
  </Card>
)

const ProgramCanvas = ({
  mode,
  form,
  activePlanId,
  onSelectPlan,
  newPlanName,
  onNewPlanNameChange,
  onAddPlan,
  planNameEditId,
  planNameDraft,
  onPlanNameDraftChange,
  onPlanNameEdit,
  onPlanNameSave,
  onPlanNameCancel,
  onPlanDuplicate,
  onPlanDelete,
  onToggleDay,
  onAddMeal,
  onMealFieldChange,
  onMealCopy,
  onMealDelete,
  onAddEmptyItem,
  onItemFieldChange,
  onItemQtyChange,
  onItemCopy,
  onItemDelete,
  onRequestNote,
  libraryTree,
  libraryExpanded,
  onToggleLibraryNode,
  onExpandAllLibrary,
  libraryFilter,
  onLibraryFilterChange,
  activeDragNode,
  onDragStart,
  onDragCancel,
  onDragEnd,
  searchPanel,
  onReset,
  onSave,
  saveLabel,
  saveLoading,
}) => {
  const activePlan = (form.mealplans || []).find((plan) => plan.id === activePlanId) || null
  const totals = calculatePlanTotals(activePlan)
  const filteredLibraryTree = useMemo(() => filterLibraryTree(libraryTree, libraryFilter), [libraryTree, libraryFilter])
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const railBoundaryRef = useRef(null)
  const sideRailAnchorRef = useRef(null)
  const rightRailRef = useRef(null)
  const [desktopRailStyle, setDesktopRailStyle] = useState(null)
  const libraryPortalTarget = typeof document !== 'undefined' ? document.body : null
  const desktopRailPinned = Boolean(desktopRailStyle)

  const collisionDetection = useCallback(
    (args) => {
      const activeType = args.active?.data?.current?.type
      if (activeType === 'libraryItem' && args.pointerCoordinates && rightRailRef.current) {
        const rect = rightRailRef.current.getBoundingClientRect()
        const { x, y } = args.pointerCoordinates
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          return []
        }
      }
      return pointerWithin(args)
    },
    []
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const updateDesktopRail = () => {
      if (window.innerWidth < 1280) {
        setDesktopRailStyle(null)
        return
      }

      const boundary = railBoundaryRef.current
      const anchor = sideRailAnchorRef.current
      const content = rightRailRef.current
      if (!boundary || !anchor || !content) {
        setDesktopRailStyle(null)
        return
      }

      const boundaryRect = boundary.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const contentRect = content.getBoundingClientRect()
      const topOffset = 24
      const bottomOffset = 24
      const viewportHeight = window.innerHeight
      const maxRailHeight = Math.max(320, viewportHeight - topOffset - bottomOffset)
      const stopPinningAt = topOffset + Math.min(contentRect.height, maxRailHeight)

      if (anchorRect.top > topOffset || boundaryRect.bottom <= stopPinningAt) {
        setDesktopRailStyle(null)
        return
      }

      setDesktopRailStyle({
        top: topOffset,
        left: anchorRect.left,
        width: anchorRect.width,
        height: Math.min(maxRailHeight, Math.max(320, boundaryRect.bottom - topOffset - bottomOffset)),
        placeholderHeight: contentRect.height,
      })
    }

    updateDesktopRail()
    window.addEventListener('scroll', updateDesktopRail, { passive: true })
    window.addEventListener('resize', updateDesktopRail)
    return () => {
      window.removeEventListener('scroll', updateDesktopRail)
      window.removeEventListener('resize', updateDesktopRail)
    }
  }, [activePlan?.id, activePlan?.meals?.length, filteredLibraryTree.length, form.mealplans.length, libraryExpanded, searchPanel])

  const renderRightRail = () => (
    <div ref={rightRailRef} className="flex flex-col gap-4 pb-8 xl:h-full xl:overflow-y-auto xl:pr-1">
      <Card className="uploader-rail-card border-0 shadow-none ring-0">
        <CardHeader><CardTitle className="text-base font-semibold text-slate-900">Food Library</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={libraryFilter} onChange={(event) => onLibraryFilterChange(event.target.value)} placeholder="Meal / Food filter" className="pl-9" />
            </div>
            <label className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={Object.values(libraryExpanded).every(Boolean) && Object.keys(libraryExpanded).length > 0} onChange={(event) => onExpandAllLibrary(event.target.checked)} className="h-4 w-4 rounded border-slate-300" />
              Expand all
            </label>
          </div>
          <div className="max-h-[32rem] space-y-3 overflow-auto pr-1">
            {filteredLibraryTree.length ? filteredLibraryTree.map((node) => {
              const expanded = libraryExpanded[node.id]
              const renderNode = (child) => !child.children?.length
                ? <LibraryLeaf key={child.id} node={child} />
                : <LibraryGroup key={child.id} node={child} expanded={libraryExpanded[child.id]} onToggle={() => onToggleLibraryNode(child.id)} childrenCount={countLeaves(child)}>{(child.children || []).map((grandChild) => renderNode(grandChild))}</LibraryGroup>
              return <LibraryGroup key={node.id} node={node} expanded={expanded} onToggle={() => onToggleLibraryNode(node.id)} childrenCount={countLeaves(node)}>{(node.children || []).map((child) => renderNode(child))}</LibraryGroup>
            }) : <EmptyState message="No library results matched the current filter." />}
          </div>
        </CardContent>
      </Card>

      {searchPanel ? <NutritionSearchPanel {...searchPanel} /> : null}

      <Card className="uploader-rail-card border-0 shadow-none ring-0">
        <CardHeader><CardTitle className="text-base font-semibold text-slate-900">Plan Totals</CardTitle></CardHeader>
        <CardContent className="space-y-2.5 pt-0 text-sm text-slate-700">
          <div className="flex items-center justify-between rounded-md bg-fitness-primary/5 px-3 py-2 font-semibold text-fitness-primary"><span>Calories</span><span>{Math.round(totals.calories)}</span></div>
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200"><span>Protein</span><span>{Math.round(totals.protein)} g</span></div>
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200"><span>Carbs</span><span>{Math.round(totals.carbs)} g</span></div>
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200"><span>Fat</span><span>{Math.round(totals.fat)} g</span></div>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div className="uploader-page">
      <Card className="uploader-rail-card border-0 shadow-none ring-0">
        <CardContent className="space-y-6 p-6">
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Name</label>
                <Input value={form.name} onChange={(event) => form.onFieldChange('name', event.target.value)} placeholder="Nutrition program name" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Nutrition Program Category</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.categoryId}
                    onChange={(event) => form.onFieldChange('categoryId', event.target.value)}
                  >
                    <option value="">Select category</option>
                    {form.categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Make Subcategory Of</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.newCategoryParentId}
                    onChange={(event) => form.onFieldChange('newCategoryParentId', event.target.value)}
                  >
                    <option value="">No parent category</option>
                    {form.categories
                      .filter((category) => !category.parent_id)
                      .map((category) => (
                        <option key={`parent-${category.id}`} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">New Category</label>
                <Input value={form.newCategoryName} onChange={(event) => form.onFieldChange('newCategoryName', event.target.value)} placeholder="Create a new category if needed" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Textarea value={form.description} onChange={(event) => form.onFieldChange('description', event.target.value)} placeholder="Program description" className="min-h-[140px]" />
              </div>
            </div>

            <div className="uploader-highlight-panel">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-fitness-primary">Builder</p>
                  <h3 className="mt-2 text-xl font-semibold text-slate-900">{mode === 'create' ? 'Design a reusable nutrition program' : 'Refine the selected nutrition program'}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Keep the category flow consistent, build one or more meal plans, and use the same drag-and-drop language the rest of the app already uses.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Meal Plans</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{form.mealplans.length}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Active Plan Calories</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{formatNumberSmart(totals.calories)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={railBoundaryRef} className="grid gap-6 xl:grid-cols-[minmax(0,2fr),minmax(320px,0.95fr)]">
            <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragCancel={onDragCancel} onDragEnd={onDragEnd}>
              <div className="space-y-5">
                <Card className="uploader-rail-card border-0 shadow-none ring-0">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <Input value={newPlanName} onChange={(event) => onNewPlanNameChange(event.target.value)} placeholder="New plan name" className="max-w-xs" />
                      <Button type="button" onClick={onAddPlan} disabled={!newPlanName.trim()}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Plan
                      </Button>
                    </div>

                    <ProgramDropZone mode={mode}>
                      {(form.mealplans || []).length ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2 border-b pb-3">
                            {form.mealplans.map((plan) => {
                              const isActive = activePlan?.id === plan.id
                              const isEditing = planNameEditId === plan.id
                              return (
                                <div key={plan.id} className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm ring-1 transition', isActive ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-white text-slate-700 ring-slate-200')}>
                                  {isEditing ? (
                                    <>
                                      <input
                                        type="text"
                                        value={planNameDraft}
                                        onChange={(event) => onPlanNameDraftChange(event.target.value)}
                                        onBlur={() => onPlanNameSave(plan.id)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault()
                                            onPlanNameSave(plan.id)
                                          }
                                          if (event.key === 'Escape') {
                                            event.preventDefault()
                                            onPlanNameCancel()
                                          }
                                        }}
                                        autoFocus
                                        className="h-8 w-40 rounded-md border border-white/40 bg-white/90 px-2 text-xs text-slate-900"
                                      />
                                      <button type="button" onClick={onPlanNameCancel} className="rounded-full bg-white/90 p-1 text-slate-500">
                                        <RotateCcw className="h-3.5 w-3.5" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button type="button" onClick={() => onSelectPlan(plan.id)} className="truncate text-left">
                                        {plan.name}
                                      </button>
                                      <button type="button" onClick={() => onPlanNameEdit(plan)} className="rounded-full p-0.5 text-inherit/80"><Pencil className="h-3.5 w-3.5" /></button>
                                      <button type="button" onClick={() => onPlanDuplicate(plan.id)} className="rounded-full p-0.5 text-inherit/80"><Copy className="h-3.5 w-3.5" /></button>
                                      <button type="button" onClick={() => onPlanDelete(plan.id)} className="rounded-full p-0.5 text-red-300 hover:text-red-200"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </>
                                  )}
                                </div>
                              )
                            })}
                          </div>

                          {activePlan ? (
                            <>
                              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Active Days</span>
                                  {DAY_ORDER.map((day) => (
                                    <label key={`${activePlan.id}-${day}`} className={cn('flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold transition', activePlan.days.includes(day) ? 'border-fitness-primary/30 bg-fitness-primary/10 text-fitness-primary' : 'border-slate-200 bg-white text-slate-500')}>
                                      <input type="checkbox" checked={activePlan.days.includes(day)} onChange={(event) => onToggleDay(activePlan.id, day, event.target.checked)} className="h-3 w-3 rounded border-slate-300" />
                                      {DAY_LABELS[day]}
                                    </label>
                                  ))}
                                </div>
                                <Button type="button" variant="outline" onClick={() => onAddMeal(activePlan.id)}>
                                  <Plus className="mr-2 h-4 w-4" />
                                  Add Meal
                                </Button>
                              </div>

                              <div className="space-y-4">
                                {activePlan.meals.map((meal) => {
                                  const mealTotals = (meal.items || []).reduce((acc, item) => {
                                    const macros = getFoodMacros(item)
                                    acc.calories += macros.calories
                                    acc.protein += macros.protein
                                    acc.carbs += macros.carbs
                                    acc.fat += macros.fat
                                    return acc
                                  }, { calories: 0, protein: 0, carbs: 0, fat: 0 })
                                  const macroPercents = {
                                    protein: mealTotals.calories ? Math.round(((mealTotals.protein * 4) / mealTotals.calories) * 100) : 0,
                                    carbs: mealTotals.calories ? Math.round(((mealTotals.carbs * 4) / mealTotals.calories) * 100) : 0,
                                    fat: mealTotals.calories ? Math.round(((mealTotals.fat * 9) / mealTotals.calories) * 100) : 0,
                                  }
                                  return (
                                    <DropZone key={meal.id} mealId={meal.id} planId={activePlan.id}>
                                      <Card className="uploader-rail-card border-0 shadow-none ring-0">
                                        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
                                          <div className="flex flex-wrap items-center gap-3">
                                            <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1 ring-1 ring-slate-200">
                                              <select value={meal.name} onChange={(event) => onMealFieldChange(activePlan.id, meal.id, 'name', event.target.value)} className="w-36 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-semibold text-slate-800">
                                                {[meal.name, ...MEAL_NAME_OPTIONS].filter((value, index, arr) => arr.indexOf(value) === index).map((option) => <option key={`${meal.id}-${option}`} value={option}>{option}</option>)}
                                              </select>
                                            </div>
                                            <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1 ring-1 ring-slate-200">
                                              <Clock3 className="h-4 w-4 text-slate-500" />
                                              <input type="time" value={meal.time} onChange={(event) => onMealFieldChange(activePlan.id, meal.id, 'time', event.target.value)} className="w-24 rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-800" />
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <button type="button" onClick={() => onMealCopy(activePlan.id, meal.id)} className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"><Copy className="h-4 w-4" /></button>
                                            <button type="button" onClick={() => onMealDelete(activePlan.id, meal.id)} className="rounded-full border border-red-200 bg-white p-2 text-red-500 transition hover:border-red-300 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                                          </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4 pt-0">
                                          <div className="overflow-x-auto">
                                            <div className="min-w-[940px] space-y-2">
                                              <div className="grid grid-cols-[auto,2.2fr,0.7fr,0.9fr,0.7fr,0.7fr,0.7fr,0.7fr,auto] items-center gap-2 rounded-xl bg-slate-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">
                                                <span />
                                                <span>Name</span>
                                                <span>Qty</span>
                                                <span>Unit</span>
                                                <span>Cals</span>
                                                <span>Protn</span>
                                                <span>Carbs</span>
                                                <span>Fat</span>
                                                <span className="text-right">Actions</span>
                                              </div>
                                              {mode === 'create' ? (
                                                <SortableContext items={(meal.items || []).map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                                  {(meal.items || []).map((item) => (
                                                    <SortableMealItem key={item.id} item={item} mealId={meal.id} planId={activePlan.id} onFieldChange={(key, value) => onItemFieldChange(activePlan.id, meal.id, item.id, key, value)} onQtyChange={(value) => onItemQtyChange(activePlan.id, meal.id, item.id, value)} onCopy={() => onItemCopy(activePlan.id, meal.id, item.id)} onDelete={() => onItemDelete(activePlan.id, meal.id, item.id)} onNote={() => onRequestNote(activePlan.id, meal.id, item)} />
                                                  ))}
                                                </SortableContext>
                                              ) : (
                                                (meal.items || []).map((item) => (
                                                  <StaticMealItem key={item.id} item={item} onFieldChange={(key, value) => onItemFieldChange(activePlan.id, meal.id, item.id, key, value)} onQtyChange={(value) => onItemQtyChange(activePlan.id, meal.id, item.id, value)} onCopy={() => onItemCopy(activePlan.id, meal.id, item.id)} onDelete={() => onItemDelete(activePlan.id, meal.id, item.id)} onNote={() => onRequestNote(activePlan.id, meal.id, item)} />
                                                ))
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-dashed border-slate-200 pt-3">
                                            <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-500">
                                              <span>Meal Totals</span>
                                              <span>Cals {formatNumberSmart(mealTotals.calories)}</span>
                                              <span>Protein {formatNumberSmart(mealTotals.protein)}</span>
                                              <span>Carbs {formatNumberSmart(mealTotals.carbs)}</span>
                                              <span>Fat {formatNumberSmart(mealTotals.fat)}</span>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500">
                                              <span>Protein {macroPercents.protein}%</span>
                                              <span>Carbs {macroPercents.carbs}%</span>
                                              <span>Fat {macroPercents.fat}%</span>
                                            </div>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    </DropZone>
                                  )
                                })}
                              </div>
                            </>
                          ) : <EmptyState message="Create or select a plan to start building meal cards." />}
                        </>
                      ) : <EmptyState message="Drop a nutrition program template here, or add a plan manually to start building meal cards." />}
                    </ProgramDropZone>
                  </CardContent>
                </Card>

                <div className="flex flex-wrap items-center justify-end gap-3">
                  <Button type="button" variant="outline" onClick={onReset}><RotateCcw className="mr-2 h-4 w-4" />Reset Changes</Button>
                  <Button type="button" onClick={onSave} disabled={saveLoading}>{saveLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{saveLabel}</Button>
                </div>
              </div>

              <div ref={sideRailAnchorRef} className="xl:self-start">
                {desktopRailPinned ? (
                  <div aria-hidden="true" className="hidden xl:block" style={{ height: desktopRailStyle.placeholderHeight }} />
                ) : (
                  <div className="flex flex-col pb-8 xl:h-[calc(100vh-3rem)] xl:overflow-hidden">
                    {renderRightRail()}
                  </div>
                )}
              </div>

              {desktopRailPinned && libraryPortalTarget
                ? createPortal(
                    <div
                      className="hidden overflow-hidden xl:flex xl:flex-col"
                      style={{
                        position: 'fixed',
                        top: desktopRailStyle.top,
                        left: desktopRailStyle.left,
                        width: desktopRailStyle.width,
                        height: desktopRailStyle.height,
                        zIndex: 20,
                      }}
                    >
                      {renderRightRail()}
                    </div>,
                    libraryPortalTarget
                  )
                : null}

              {libraryPortalTarget
                ? createPortal(
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <LibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>,
                    libraryPortalTarget
                  )
                : (
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <LibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>
                  )}
            </DndContext>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function NutritionProgramsWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const [categories, setCategories] = useState([])
  const [allCategoryRows, setAllCategoryRows] = useState([])
  const [categoryRows, setCategoryRows] = useState([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [loadingCategoryRows, setLoadingCategoryRows] = useState(false)
  const [updatingCategoryId, setUpdatingCategoryId] = useState(null)

  const [libraryTree, setLibraryTree] = useState([])
  const [libraryExpanded, setLibraryExpanded] = useState({})
  const [libraryFilter, setLibraryFilter] = useState('')

  const [createFormState, setCreateFormState] = useState({ name: '', description: '', categoryId: '', newCategoryName: '', newCategoryParentId: '', mealplans: [] })
  const [createPlanName, setCreatePlanName] = useState('')
  const [activeCreatePlanId, setActiveCreatePlanId] = useState(null)
  const [createPlanNameEditId, setCreatePlanNameEditId] = useState(null)
  const [createPlanNameDraft, setCreatePlanNameDraft] = useState('')
  const [createSaving, setCreateSaving] = useState(false)
  const [createDragNode, setCreateDragNode] = useState(null)

  const [programRows, setProgramRows] = useState([])
  const [programSearch, setProgramSearch] = useState('')
  const [programPagination, setProgramPagination] = useState({ ...EMPTY_PAGINATION })
  const [programTotalCount, setProgramTotalCount] = useState(0)
  const [selectedProgramIds, setSelectedProgramIds] = useState([])
  const [loadingProgramRows, setLoadingProgramRows] = useState(false)
  const [selectedProgramId, setSelectedProgramId] = useState(null)
  const [loadingProgramDetail, setLoadingProgramDetail] = useState(false)
  const [editFormState, setEditFormState] = useState(null)
  const [editBaseline, setEditBaseline] = useState(null)
  const [activeEditPlanId, setActiveEditPlanId] = useState(null)
  const [editPlanName, setEditPlanName] = useState('')
  const [editPlanNameEditId, setEditPlanNameEditId] = useState(null)
  const [editPlanNameDraft, setEditPlanNameDraft] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editDragNode, setEditDragNode] = useState(null)
  const [editTargetMealId, setEditTargetMealId] = useState('')
  const [editCatalogCollapsed, setEditCatalogCollapsed] = useState(false)
  const [deleteCatalogDialogOpen, setDeleteCatalogDialogOpen] = useState(false)
  const [deletingCatalogRows, setDeletingCatalogRows] = useState(false)
  const [deletedEditPlanIds, setDeletedEditPlanIds] = useState([])
  const [deletedMealFoodIdsByMeal, setDeletedMealFoodIdsByMeal] = useState({})

  const [nutritionSearch, setNutritionSearch] = useState('')
  const [nutritionPagination, setNutritionPagination] = useState({ ...EMPTY_SEARCH_PAGINATION })
  const [nutritionResults, setNutritionResults] = useState([])
  const [nutritionTotalCount, setNutritionTotalCount] = useState(0)
  const [expandedNutritionResultId, setExpandedNutritionResultId] = useState(null)
  const [nutritionDetailMap, setNutritionDetailMap] = useState({})
  const [nutritionLoading, setNutritionLoading] = useState(false)
  const [nutritionDetailLoadingId, setNutritionDetailLoadingId] = useState(null)

  const [noteDialog, setNoteDialog] = useState(null)
  const [noteDraft, setNoteDraft] = useState('')

  const visibleNutritionRows = nutritionResults.slice(
    nutritionPagination.pageIndex * nutritionPagination.pageSize,
    nutritionPagination.pageIndex * nutritionPagination.pageSize + nutritionPagination.pageSize
  )

  const topLevelCategories = useMemo(() => allCategoryRows.filter((row) => !row.parent_id), [allCategoryRows])
  const categoryFilterOptions = useMemo(() => {
    const source = categories.length ? categories : allCategoryRows
    return source
      .filter((row) => row?.id && row?.name)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
  }, [allCategoryRows, categories])
  const selectedProgramRow = useMemo(() => programRows.find((row) => row.id === selectedProgramId) || null, [programRows, selectedProgramId])

  const buildFormBridge = useCallback((state, setState) => ({
    ...state,
    categories,
    onFieldChange: (key, value) => {
      setState((current) => {
        const next = { ...current, [key]: value }
        if (key === 'categoryId' && value !== '') {
          next.newCategoryName = ''
          next.newCategoryParentId = ''
        } else if ((key === 'newCategoryName' || key === 'newCategoryParentId') && `${value}`.trim() !== '') {
          next.categoryId = ''
        }
        return next
      })
    },
  }), [categories])

  const createForm = useMemo(() => buildFormBridge(createFormState, setCreateFormState), [buildFormBridge, createFormState])
  const editForm = useMemo(() => (editFormState ? buildFormBridge(editFormState, setEditFormState) : null), [buildFormBridge, editFormState])

  const loadCategories = useCallback(async () => {
    if (!companyUrl) return
    const response = await apiService.fetchNutritionProgramCategories(companyUrl)
    if (!response.success) {
      toast({ title: 'Unable to load nutrition program categories', description: response.error, variant: 'destructive' })
      return
    }
    const rows = Array.isArray(response.data) ? response.data : []
    setCategories(rows)
    setAllCategoryRows(rows)
  }, [companyUrl, toast])

  const loadCategoryRows = useCallback(async (selectedCategoryId = 'all') => {
    if (!companyUrl) return
    setLoadingCategoryRows(true)
    const response = await apiService.fetchNutritionProgramCategories(companyUrl, selectedCategoryId === 'all' ? null : selectedCategoryId)
    setLoadingCategoryRows(false)
    if (!response.success) {
      toast({ title: 'Unable to load category layout', description: response.error, variant: 'destructive' })
      return
    }
    const rows = Array.isArray(response.data) ? response.data : []
    setCategoryRows(rows)
    if (selectedCategoryId === 'all') setAllCategoryRows(rows)
  }, [companyUrl, toast])

  const loadProgramRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingProgramRows(true)
    const response = await apiService.fetchNutritionPrograms(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      limit: programPagination.pageSize,
      fromIndex: programPagination.pageIndex * programPagination.pageSize,
      searchtext: programSearch.trim(),
    })
    setLoadingProgramRows(false)
    if (!response.success) {
      toast({ title: 'Unable to load nutrition programs', description: response.error, variant: 'destructive' })
      return
    }
    setProgramRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setProgramTotalCount(Number(response.data?.count) || 0)
    setSelectedProgramIds([])
  }, [companyUrl, programPagination.pageIndex, programPagination.pageSize, programSearch, toast])

  const loadLibraryTree = useCallback(async () => {
    if (!companyUrl) return
    const response = await apiService.fetchNutritionProgramTree(companyUrl)
    if (!response.success) {
      toast({ title: 'Unable to load food library', description: response.error, variant: 'destructive' })
      return
    }
    const normalized = normalizeNutritionProgramTree(response.data)
    setLibraryTree(normalized)
    setLibraryExpanded(buildLibraryExpansionState(normalized, false))
  }, [companyUrl, toast])

  const loadProgramDetail = useCallback(async (programId, row = null) => {
    if (!companyUrl || !programId) return
    setSelectedProgramId(programId)
    setLoadingProgramDetail(true)
    const response = await apiService.fetchNutritionProgramMealPlans(companyUrl, programId)
    setLoadingProgramDetail(false)
    if (!response.success) {
      toast({ title: 'Unable to load nutrition program details', description: response.error, variant: 'destructive' })
      return
    }
    const normalized = normalizeProgramDetail(response.data, row)
    setEditFormState(normalized)
    setEditBaseline(normalized)
    setDeletedEditPlanIds([])
    setDeletedMealFoodIdsByMeal({})
    setActiveEditPlanId(normalized.mealplans?.[0]?.id || null)
    setEditTargetMealId(normalized.mealplans?.[0]?.meals?.[0]?.id || '')
    setNutritionSearch('')
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
    setEditCatalogCollapsed(true)
  }, [companyUrl, toast])

  useEffect(() => {
    loadCategories()
    loadCategoryRows('all')
    loadLibraryTree()
    loadProgramRows()
  }, [loadCategories, loadCategoryRows, loadLibraryTree, loadProgramRows])

  useEffect(() => {
    if (!nutritionSearch.trim() || activeTab !== 'edit' || !editTargetMealId) {
      setNutritionResults([])
      setNutritionTotalCount(0)
      setExpandedNutritionResultId(null)
      return
    }
    const timer = window.setTimeout(async () => {
      setNutritionLoading(true)
      const response = await apiService.searchNewNutrition(companyUrl, nutritionSearch.trim())
      setNutritionLoading(false)
      if (!response.success) {
        toast({ title: 'Unable to search foods', description: response.error, variant: 'destructive' })
        return
      }
      const rows = Array.isArray(response.data?.results) ? response.data.results.map(buildNutritionSearchDraft) : []
      setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
      setNutritionResults(rows)
      setNutritionTotalCount(Number(response.data?.total) || rows.length)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [activeTab, companyUrl, editTargetMealId, nutritionSearch, toast])

  const updateMealplans = useCallback((scope, updater) => {
    if (scope === 'create') {
      setCreateFormState((current) => ({ ...current, mealplans: updater(current.mealplans || []) }))
      return
    }
    setEditFormState((current) => (current ? { ...current, mealplans: updater(current.mealplans || []) } : current))
  }, [])

  const updatePlanField = useCallback((scope, planId, updater) => {
    updateMealplans(scope, (plans) => plans.map((plan) => (plan.id === planId ? updater(plan) : plan)))
  }, [updateMealplans])

  const updateMealField = useCallback((scope, planId, mealId, key, value) => {
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) => (meal.id === mealId ? { ...meal, [key]: value } : meal)),
    }))
  }, [updatePlanField])

  const updateItemField = useCallback((scope, planId, mealId, itemId, key, value) => {
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) =>
        meal.id === mealId
          ? { ...meal, items: (meal.items || []).map((item) => (item.id === itemId ? { ...item, [key]: key === 'qty' ? Math.max(parseNumberField(value), 0) : value } : item)) }
          : meal
      ),
    }))
  }, [updatePlanField])

  const handleAddPlan = (scope) => {
    const name = (scope === 'create' ? createPlanName : editPlanName).trim()
    if (!name) {
      toast({ title: 'Plan name is required', variant: 'destructive' })
      return
    }
    const nextPlan = createProgramPlanDraft(name)
    updateMealplans(scope, (plans) => [...plans, nextPlan])
    if (scope === 'create') {
      setActiveCreatePlanId(nextPlan.id)
      setCreatePlanName('')
    } else {
      setActiveEditPlanId(nextPlan.id)
      setEditPlanName('')
    }
  }

  const handleDuplicatePlan = (scope, planId) => updateMealplans(scope, (plans) => {
    const target = plans.find((plan) => plan.id === planId)
    if (!target) return plans
    const duplicate = {
      ...target,
      id: createDraftId('plan-copy'),
      persistedId: null,
      name: `Copy of ${target.name}`,
      meals: (target.meals || []).map((meal) => ({
        ...meal,
        id: createDraftId('meal-copy'),
        persistedId: null,
        items: (meal.items || []).map((item) => ({ ...item, id: createDraftId('food-copy'), persistedId: null })),
      })),
    }
    return [...plans, duplicate]
  })

  const handleDeletePlan = (scope, planId) => {
    if (scope === 'edit') {
      const targetPlan = (editFormState?.mealplans || []).find((plan) => plan.id === planId)
      if (targetPlan?.persistedId) {
        setDeletedEditPlanIds((current) => (current.includes(targetPlan.persistedId) ? current : [...current, targetPlan.persistedId]))
      }
      if (targetPlan?.meals?.length) {
        setDeletedMealFoodIdsByMeal((current) => {
          const next = { ...current }
          targetPlan.meals.forEach((meal) => {
            if (meal?.persistedId) delete next[meal.persistedId]
          })
          return next
        })
      }
    }
    updateMealplans(scope, (plans) => plans.filter((plan) => plan.id !== planId))
    if (scope === 'create' && activeCreatePlanId === planId) setActiveCreatePlanId(null)
    if (scope === 'edit' && activeEditPlanId === planId) setActiveEditPlanId(null)
  }

  const handleToggleDay = (scope, planId, day, checked) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      days: DAY_ORDER.filter((label) => (label === day ? checked : plan.days.includes(label))),
    }))

  const handleAddMeal = (scope, planId) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: [...(plan.meals || []), createMealDraft({ name: 'Snack', time: '12:00' })],
    }))

  const handleDuplicateMeal = (scope, planId, mealId) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).flatMap((meal) =>
        meal.id === mealId
          ? [
              meal,
              {
                ...meal,
                id: createDraftId('meal-copy'),
                persistedId: null,
                items: (meal.items || []).map((item) => ({ ...item, id: createDraftId('food-copy'), persistedId: null })),
              },
            ]
          : [meal]
      ),
    }))

  const handleDeleteMeal = (scope, planId, mealId) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).filter((meal) => meal.id !== mealId),
    }))

  const handleAddEmptyItem = (scope, planId, mealId) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) =>
        meal.id === mealId
          ? { ...meal, items: [...(meal.items || []), buildProgramFoodDraft({ name: '', qty: 1, unit: '' }, 'manual', { name: '', unit: '' })] }
          : meal
      ),
    }))

  const handleCopyItem = (scope, planId, mealId, itemId) =>
    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) =>
        meal.id === mealId
          ? {
              ...meal,
              items: (meal.items || []).flatMap((item) =>
                item.id === itemId ? [item, { ...item, id: createDraftId('food-copy'), persistedId: null }] : [item]
              ),
            }
          : meal
      ),
    }))

  const handleDeleteItem = (scope, planId, mealId, itemId) => {
    if (scope === 'edit') {
      const targetPlan = (editFormState?.mealplans || []).find((plan) => plan.id === planId)
      const targetMeal = targetPlan?.meals?.find((meal) => meal.id === mealId)
      const targetItem = targetMeal?.items?.find((item) => item.id === itemId)
      if (targetMeal?.persistedId && targetItem?.persistedId) {
        setDeletedMealFoodIdsByMeal((current) => ({
          ...current,
          [targetMeal.persistedId]: current[targetMeal.persistedId]?.includes(targetItem.persistedId)
            ? current[targetMeal.persistedId]
            : [...(current[targetMeal.persistedId] || []), targetItem.persistedId],
        }))
      }
    }

    updatePlanField(scope, planId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) =>
        meal.id === mealId ? { ...meal, items: (meal.items || []).filter((item) => item.id !== itemId) } : meal
      ),
    }))
  }

  const handleOpenNote = (scope, planId, mealId, item) => {
    setNoteDialog({ scope, planId, mealId, itemId: item.id })
    setNoteDraft(item.note || '')
  }

  const handleSaveNote = () => {
    if (!noteDialog) return
    updateItemField(noteDialog.scope, noteDialog.planId, noteDialog.mealId, noteDialog.itemId, 'note', noteDraft)
    setNoteDialog(null)
    setNoteDraft('')
  }

  const clonePlanForImport = useCallback((plan) => ({
    ...plan,
    id: createDraftId('plan-import'),
    persistedId: null,
    name: plan.name || 'Imported Plan',
    meals: (plan.meals || []).map((meal) => ({
      ...meal,
      id: createDraftId('meal-import'),
      persistedId: null,
      items: (meal.items || []).map((item) => ({
        ...item,
        id: createDraftId('food-import'),
        persistedId: null,
      })),
    })),
  }), [])

  const handleDragEndFactory = (scope) => async (event) => {
    const { active, over } = event
    const clearDragState = () => {
      if (scope === 'create') setCreateDragNode(null)
      else setEditDragNode(null)
    }

    try {
      const activeType = active.data?.current?.type
      const overType = over?.data?.current?.type
      const node = active.data?.current?.node
      const nodeType = activeType === 'libraryItem' && node ? classifyLibraryDropNode(node) : null
      const scopeMealplans = scope === 'create' ? createFormState.mealplans || [] : editFormState?.mealplans || []

      if (activeType === 'mealItem' && overType === 'mealItem') {
        const activeMealId = active.data.current.mealId
        const overMealId = over.data.current.mealId
        const activePlanId = active.data.current.planId
        if (activeMealId && activeMealId === overMealId && activePlanId) {
          updatePlanField(scope, activePlanId, (plan) => ({
            ...plan,
            meals: (plan.meals || []).map((meal) => {
              if (meal.id !== activeMealId) return meal
              const items = meal.items || []
              const oldIndex = items.findIndex((item) => item.id === active.id)
              const newIndex = items.findIndex((item) => item.id === over.id)
              return oldIndex === -1 || newIndex === -1 || oldIndex === newIndex ? meal : { ...meal, items: arrayMove(items, oldIndex, newIndex) }
            }),
          }))
        }
        return
      }

      if (activeType !== 'libraryItem' || !node) return

      const shouldImportProgramPlan =
        nodeType === 'program-plan' &&
        node.sourceId &&
        (overType === 'planCanvas' || overType === 'mealDrop' || overType === 'mealItem' || (!over && scopeMealplans.length === 0))

      if (shouldImportProgramPlan) {
        const response = await apiService.fetchNutritionProgramMealPlans(companyUrl, node.sourceId)
        if (!response.success) {
          toast({ title: 'Unable to import nutrition program template', description: response.error, variant: 'destructive' })
          return
        }

        const normalized = normalizeProgramDetail(response.data, { id: node.sourceId, name: node.name })
        const importedPlans = (normalized.mealplans || []).map(clonePlanForImport)
        if (!importedPlans.length) return

        updateMealplans(scope, (plans) => [...plans, ...importedPlans])
        if (scope === 'create') {
          setActiveCreatePlanId(importedPlans[0].id)
        } else {
          setActiveEditPlanId(importedPlans[0].id)
          setEditTargetMealId(importedPlans[0].meals?.[0]?.id || '')
        }
        return
      }

      if (!(overType === 'mealDrop' || overType === 'mealItem')) return

      const targetMealId = over.data?.current?.mealId
      const targetPlanId = over.data?.current?.planId
      if (!targetMealId || !targetPlanId) return

      if (nodeType === 'meal-template' && node.sourceId) {
        const response = await apiService.fetchMealTemplateFoods(companyUrl, node.sourceId)
        if (!response.success) {
          toast({ title: 'Unable to expand meal template', description: response.error, variant: 'destructive' })
          return
        }

        const templateFoods = Array.isArray(response.data?.mealtemplatefoods) ? response.data.mealtemplatefoods : []
        const itemsToAdd = templateFoods.map((food) =>
          buildProgramFoodDraft(food, 'meal-template', {
            name: food?.name || 'Food',
            unit: food?.unit || '',
            qty: food?.qty ?? food?.quantity ?? 1,
            baseQty: food?.qty ?? food?.quantity ?? 1,
            baseCalories: food?.calories,
            baseProtein: food?.protein,
            baseCarbs: food?.carbs,
            baseFat: food?.fat,
            detail: food,
          })
        )
        updatePlanField(scope, targetPlanId, (plan) => ({
          ...plan,
          meals: (plan.meals || []).map((meal) => (meal.id === targetMealId ? { ...meal, items: [...(meal.items || []), ...itemsToAdd] } : meal)),
        }))
        return
      }

      const itemsToAdd = flattenLibraryNode(node).map((leaf) =>
        buildProgramFoodDraft(leaf, 'library', {
          name: leaf.name || 'Food',
          unit: leaf.unit || '',
          qty: leaf.qty ?? 1,
          baseQty: leaf.qty ?? 1,
          baseCalories: leaf.calories,
          baseProtein: leaf.protein,
          baseCarbs: leaf.carbs,
          baseFat: leaf.fat,
          detail: leaf,
        })
      )
      updatePlanField(scope, targetPlanId, (plan) => ({
        ...plan,
        meals: (plan.meals || []).map((meal) => (meal.id === targetMealId ? { ...meal, items: [...(meal.items || []), ...itemsToAdd] } : meal)),
      }))
    } finally {
      clearDragState()
    }
  }

  const handleToggleNutritionResult = async (row) => {
    if (expandedNutritionResultId === row.id) {
      setExpandedNutritionResultId(null)
      return
    }
    setExpandedNutritionResultId(row.id)
    if (nutritionDetailMap[row.id]) return
    setNutritionDetailLoadingId(row.id)
    const response = await apiService.fetchNutritionWithDetails(companyUrl, row.raw || row.name)
    setNutritionDetailLoadingId(null)
    if (!response.success) {
      toast({ title: 'Unable to load nutrient detail', description: response.error, variant: 'destructive' })
      return
    }
    const detail = Array.isArray(response.data?.foods) ? response.data.foods[0] : null
    setNutritionDetailMap((current) => ({ ...current, [row.id]: detail }))
  }

  const handleAddNutritionFoodToEdit = (detail, measure) => {
    if (!editTargetMealId || !activeEditPlanId) {
      toast({ title: 'Select a target meal first', variant: 'destructive' })
      return
    }
    updatePlanField('edit', activeEditPlanId, (plan) => ({
      ...plan,
      meals: (plan.meals || []).map((meal) =>
        meal.id === editTargetMealId ? { ...meal, items: [...(meal.items || []), buildProgramFoodFromNutritionMeasure(detail, measure)] } : meal
      ),
    }))
    setNutritionSearch('')
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
  }

  const handleSaveCreate = async () => {
    const hasCategory = Boolean(createFormState.categoryId)
    const hasNewCategory = Boolean(createFormState.newCategoryName.trim())
    if (!createFormState.name.trim()) return toast({ title: 'Program name is required', variant: 'destructive' })
    if (!hasCategory && !hasNewCategory) return toast({ title: 'Select a nutrition program category or create a new one', variant: 'destructive' })
    if (hasCategory && (createFormState.newCategoryName.trim() || createFormState.newCategoryParentId)) return toast({ title: 'Use either an existing category or a new category flow', variant: 'destructive' })
    if (!createFormState.mealplans.length) return toast({ title: 'Add at least one meal plan before saving', variant: 'destructive' })
    if (createFormState.mealplans.some((plan) => !plan.name.trim())) return toast({ title: 'Every plan needs a name', variant: 'destructive' })

    setCreateSaving(true)
    const response = await apiService.createNutritionProgram(companyUrl, buildNutritionProgramPayload(createFormState))
    setCreateSaving(false)
    if (!response.success) return toast({ title: 'Unable to create nutrition program', description: response.error, variant: 'destructive' })

    toast({ title: 'Nutrition program created successfully' })
    setCreateFormState({ name: '', description: '', categoryId: '', newCategoryName: '', newCategoryParentId: '', mealplans: [] })
    setCreatePlanName('')
    setActiveCreatePlanId(null)
    await Promise.all([loadCategories(), loadCategoryRows(categoryFilter), loadProgramRows()])
    onTabChange('edit')
  }

  const handleSelectProgram = async (row) => row?.id && loadProgramDetail(row.id, row)

  const closeEditWorkspace = useCallback(() => {
    setSelectedProgramId(null)
    setLoadingProgramDetail(false)
    setEditFormState(null)
    setEditBaseline(null)
    setActiveEditPlanId(null)
    setEditPlanName('')
    setEditPlanNameEditId(null)
    setEditPlanNameDraft('')
    setEditDragNode(null)
    setEditTargetMealId('')
    setNutritionSearch('')
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
    setNutritionDetailMap({})
    setNutritionDetailLoadingId(null)
    setEditCatalogCollapsed(false)
    setDeleteCatalogDialogOpen(false)
    setDeletedEditPlanIds([])
    setDeletedMealFoodIdsByMeal({})
  }, [])

  const handleDeleteSelectedPrograms = async () => {
    if (!selectedProgramIds.length) return
    setDeletingCatalogRows(true)
    const response = await apiService.deleteNutritionPrograms(companyUrl, selectedProgramIds)
    setDeletingCatalogRows(false)
    if (!response.success) {
      return toast({ title: 'Unable to delete nutrition programs', description: response.error, variant: 'destructive' })
    }

    if (selectedProgramId && selectedProgramIds.includes(selectedProgramId)) closeEditWorkspace()
    setDeleteCatalogDialogOpen(false)
    toast({ title: 'Selected nutrition programs deleted' })
    await Promise.all([loadProgramRows(), loadCategoryRows(categoryFilter)])
  }

  const handleSaveEdit = async () => {
    if (!editFormState?.id) return
    if (!editFormState.name.trim()) return toast({ title: 'Program name is required', variant: 'destructive' })
    setEditSaving(true)

    const baseline = editBaseline || {}
    const requestQueue = []
    const deletedPlanIds = Array.from(new Set(deletedEditPlanIds))
    const deletedFoodEntries = Object.entries(deletedMealFoodIdsByMeal).filter(([, ids]) => Array.isArray(ids) && ids.length)

    if (!areEqualByJson(buildNutritionProgramComparable(editFormState), buildNutritionProgramComparable(baseline))) {
      requestQueue.push(apiService.updateNutritionProgram(companyUrl, editFormState.id, buildNutritionProgramUpdatePayload(editFormState)))
    }

    if (deletedPlanIds.length) {
      requestQueue.push(apiService.deleteNutritionProgramMealPlans(companyUrl, editFormState.id, deletedPlanIds))
    }

    deletedFoodEntries.forEach(([mealId, foodIds]) => {
      requestQueue.push(apiService.deleteNutritionProgramMealFoods(companyUrl, mealId, Array.from(new Set(foodIds))))
    })

    ;(editFormState.mealplans || []).forEach((plan) => {
      if (!plan.persistedId) return
      if (deletedPlanIds.includes(plan.persistedId)) return
      const baselinePlan = (baseline.mealplans || []).find((entry) => entry.persistedId === plan.persistedId)

      if (!baselinePlan || !areEqualByJson(buildNutritionMealPlanComparable(plan), buildNutritionMealPlanComparable(baselinePlan))) {
        requestQueue.push(apiService.updateNutritionProgramMealPlan(companyUrl, editFormState.id, plan.persistedId, buildNutritionMealPlanPayload(plan)))
      }

      ;(plan.meals || []).forEach((meal) => {
        const baselineMeal = baselinePlan?.meals?.find((entry) => entry.persistedId === meal.persistedId)
        ;(meal.items || []).forEach((item) => {
          if (!meal.persistedId || !item.persistedId) return
          if ((deletedMealFoodIdsByMeal[meal.persistedId] || []).includes(item.persistedId)) return
          const baselineItem = baselineMeal?.items?.find((entry) => entry.persistedId === item.persistedId)
          if (!baselineItem || !areEqualByJson(buildNutritionMealFoodComparable(item), buildNutritionMealFoodComparable(baselineItem))) {
            requestQueue.push(apiService.updateNutritionProgramMealFood(companyUrl, meal.persistedId, item.persistedId, buildNutritionMealFoodUpdatePayload(item)))
          }
        })
      })
    })

    const results = requestQueue.length ? await Promise.all(requestQueue) : []
    setEditSaving(false)

    const failed = results.find((result) => !result.success)
    if (failed) {
      return toast({ title: 'Some nutrition changes failed to save', description: failed.error, variant: 'destructive' })
    }

    if (!requestQueue.length) {
      toast({ title: 'No nutrition changes to save' })
      return
    }

    toast({ title: 'Nutrition program updated' })
    await loadProgramRows()
    closeEditWorkspace()
  }

  const handleCategoryParentChange = async (categoryId, value) => {
    setUpdatingCategoryId(categoryId)
    const response = await apiService.updateNutritionProgramCategory(companyUrl, categoryId, { parent_id: value ? Number(value) : null })
    setUpdatingCategoryId(null)
    if (!response.success) return toast({ title: 'Unable to update category', description: response.error, variant: 'destructive' })
    toast({ title: 'Category updated successfully' })
    await Promise.all([loadCategories(), loadCategoryRows(categoryFilter)])
  }

  const editTargetMealOptions = useMemo(() => {
    const activePlan = (editFormState?.mealplans || []).find((plan) => plan.id === activeEditPlanId)
    return (activePlan?.meals || []).map((meal) => ({ id: meal.id, label: `${meal.name || 'Meal'} ${meal.time ? `• ${meal.time}` : ''}` }))
  }, [activeEditPlanId, editFormState?.mealplans])

  const renderProgramCatalogCard = (collapsed = false) => {
    if (collapsed) {
      return (
        <Card className="border-slate-200 shadow-sm xl:sticky xl:top-6">
          <CardContent className="flex flex-col items-center gap-4 p-3">
            <button
              type="button"
              onClick={() => setEditCatalogCollapsed(false)}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-fitness-primary/20 bg-fitness-primary/10 text-fitness-primary transition hover:bg-fitness-primary/15"
              title="Browse nutrition programs"
            >
              <LayoutGrid className="h-5 w-5" />
            </button>
            <div className="w-full rounded-lg bg-slate-50 px-2 py-3 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Editing</p>
              <p className="mt-2 break-words text-xs font-semibold text-slate-900">{editFormState?.name || 'Program'}</p>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="text-lg">Nutrition Program Catalog</CardTitle>
              <CardDescription>Search saved programs, load a full builder on the right, and edit the nested meal plan structure.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={programSearch}
                  onChange={(event) => {
                    setProgramPagination((current) => ({ ...current, pageIndex: 0 }))
                    setProgramSearch(event.target.value)
                  }}
                  placeholder="Search"
                  className="w-[220px] pl-9"
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                disabled={!selectedProgramIds.length}
                onClick={() => setDeleteCatalogDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Selected
              </Button>
              {editForm ? (
                <Button type="button" variant="outline" onClick={() => setEditCatalogCollapsed(true)}>
                  Focus Editor
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingProgramRows ? <EmptyState message="Loading nutrition programs..." /> : programRows.length ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={programRows.length > 0 && programRows.every((row) => selectedProgramIds.includes(row.id))}
                          onChange={(event) => setSelectedProgramIds(event.target.checked ? programRows.map((row) => row.id) : [])}
                        />
                      </th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {programRows.map((row) => (
                      <tr key={row.id} onClick={() => handleSelectProgram(row)} className={cn('cursor-pointer transition hover:bg-slate-50', selectedProgramId === row.id && 'bg-fitness-primary/5')}>
                        <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedProgramIds.includes(row.id)}
                            onChange={() => setSelectedProgramIds((current) => current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id])}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                        <td className="px-4 py-3 text-slate-600">{row.nutritionprogramcategory?.name || 'Uncategorized'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.description || 'No description'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationFooter pageIndex={programPagination.pageIndex} pageSize={programPagination.pageSize} totalCount={programTotalCount} onPageChange={(pageIndex) => setProgramPagination((current) => ({ ...current, pageIndex }))} />
            </>
          ) : <EmptyState message="No nutrition programs matched the current search." />}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="uploader-page">
      <PageHero
        title="Nutrition Program Templates"
        description="Create reusable nutrition programs, edit saved program structures, and keep category layout aligned with the rest of the uploader theme."
        onBack={onBack}
      />
      <WorkspaceTabs tabs={NUTRITION_PROGRAM_TABS} activeTab={activeTab} onChange={onTabChange} />
      <div className="uploader-surface space-y-6">

      {activeTab === 'create' ? (
        <ProgramCanvas
          mode="create"
          form={createForm}
          activePlanId={activeCreatePlanId}
          onSelectPlan={setActiveCreatePlanId}
          newPlanName={createPlanName}
          onNewPlanNameChange={setCreatePlanName}
          onAddPlan={() => handleAddPlan('create')}
          planNameEditId={createPlanNameEditId}
          planNameDraft={createPlanNameDraft}
          onPlanNameDraftChange={setCreatePlanNameDraft}
          onPlanNameEdit={(plan) => {
            setCreatePlanNameEditId(plan.id)
            setCreatePlanNameDraft(plan.name)
          }}
          onPlanNameSave={(planId) => {
            updatePlanField('create', planId, (plan) => ({ ...plan, name: createPlanNameDraft.trim() || plan.name }))
            setCreatePlanNameEditId(null)
            setCreatePlanNameDraft('')
          }}
          onPlanNameCancel={() => {
            setCreatePlanNameEditId(null)
            setCreatePlanNameDraft('')
          }}
          onPlanDuplicate={(planId) => handleDuplicatePlan('create', planId)}
          onPlanDelete={(planId) => handleDeletePlan('create', planId)}
          onToggleDay={(planId, day, checked) => handleToggleDay('create', planId, day, checked)}
          onAddMeal={(planId) => handleAddMeal('create', planId)}
          onMealFieldChange={(planId, mealId, key, value) => updateMealField('create', planId, mealId, key, value)}
          onMealCopy={(planId, mealId) => handleDuplicateMeal('create', planId, mealId)}
          onMealDelete={(planId, mealId) => handleDeleteMeal('create', planId, mealId)}
          onAddEmptyItem={(planId, mealId) => handleAddEmptyItem('create', planId, mealId)}
          onItemFieldChange={(planId, mealId, itemId, key, value) => updateItemField('create', planId, mealId, itemId, key, value)}
          onItemQtyChange={(planId, mealId, itemId, value) => updateItemField('create', planId, mealId, itemId, 'qty', value)}
          onItemCopy={(planId, mealId, itemId) => handleCopyItem('create', planId, mealId, itemId)}
          onItemDelete={(planId, mealId, itemId) => handleDeleteItem('create', planId, mealId, itemId)}
          onRequestNote={(planId, mealId, item) => handleOpenNote('create', planId, mealId, item)}
          libraryTree={libraryTree}
          libraryExpanded={libraryExpanded}
          onToggleLibraryNode={(nodeId) => setLibraryExpanded((current) => ({ ...current, [nodeId]: !current[nodeId] }))}
          onExpandAllLibrary={(checked) => setLibraryExpanded(buildLibraryExpansionState(libraryTree, checked))}
          libraryFilter={libraryFilter}
          onLibraryFilterChange={setLibraryFilter}
          activeDragNode={createDragNode}
          onDragStart={(event) => event.active.data?.current?.type === 'libraryItem' && setCreateDragNode(event.active.data.current.node)}
          onDragCancel={() => setCreateDragNode(null)}
          onDragEnd={handleDragEndFactory('create')}
          searchPanel={null}
          onReset={() => {
            setCreateFormState({ name: '', description: '', categoryId: '', newCategoryName: '', newCategoryParentId: '', mealplans: [] })
            setCreatePlanName('')
            setActiveCreatePlanId(null)
          }}
          onSave={handleSaveCreate}
          saveLabel="Save Nutrition Program"
          saveLoading={createSaving}
        />
      ) : null}

      {activeTab === 'edit' ? (
        <div className={cn('grid gap-6', editForm && editCatalogCollapsed ? 'xl:grid-cols-[88px,minmax(0,1fr)]' : 'grid-cols-1')}>
          {editForm && editCatalogCollapsed ? (
            <div className="space-y-6">
              {renderProgramCatalogCard(true)}
            </div>
          ) : (
            <div className="space-y-6">
              {renderProgramCatalogCard(false)}
            </div>
          )}

          {editForm && editCatalogCollapsed ? (
            <div className="space-y-6">

              {loadingProgramDetail ? <EmptyState message="Loading nutrition program detail..." /> : (
                <ProgramCanvas
                  mode="edit"
                  form={editForm}
                  activePlanId={activeEditPlanId}
                  onSelectPlan={setActiveEditPlanId}
                  newPlanName={editPlanName}
                  onNewPlanNameChange={setEditPlanName}
                  onAddPlan={() => handleAddPlan('edit')}
                  planNameEditId={editPlanNameEditId}
                  planNameDraft={editPlanNameDraft}
                  onPlanNameDraftChange={setEditPlanNameDraft}
                  onPlanNameEdit={(plan) => { setEditPlanNameEditId(plan.id); setEditPlanNameDraft(plan.name) }}
                  onPlanNameSave={(planId) => { updatePlanField('edit', planId, (plan) => ({ ...plan, name: editPlanNameDraft.trim() || plan.name })); setEditPlanNameEditId(null); setEditPlanNameDraft('') }}
                  onPlanNameCancel={() => { setEditPlanNameEditId(null); setEditPlanNameDraft('') }}
                  onPlanDuplicate={(planId) => handleDuplicatePlan('edit', planId)}
                  onPlanDelete={(planId) => handleDeletePlan('edit', planId)}
                  onToggleDay={(planId, day, checked) => handleToggleDay('edit', planId, day, checked)}
                  onAddMeal={(planId) => handleAddMeal('edit', planId)}
                  onMealFieldChange={(planId, mealId, key, value) => updateMealField('edit', planId, mealId, key, value)}
                  onMealCopy={(planId, mealId) => handleDuplicateMeal('edit', planId, mealId)}
                  onMealDelete={(planId, mealId) => handleDeleteMeal('edit', planId, mealId)}
                  onAddEmptyItem={(planId, mealId) => { handleAddEmptyItem('edit', planId, mealId); if (activeEditPlanId === planId) setEditTargetMealId(mealId) }}
                  onItemFieldChange={(planId, mealId, itemId, key, value) => updateItemField('edit', planId, mealId, itemId, key, value)}
                  onItemQtyChange={(planId, mealId, itemId, value) => updateItemField('edit', planId, mealId, itemId, 'qty', value)}
                  onItemCopy={(planId, mealId, itemId) => handleCopyItem('edit', planId, mealId, itemId)}
                  onItemDelete={(planId, mealId, itemId) => handleDeleteItem('edit', planId, mealId, itemId)}
                  onRequestNote={(planId, mealId, item) => handleOpenNote('edit', planId, mealId, item)}
                  libraryTree={libraryTree}
                  libraryExpanded={libraryExpanded}
                  onToggleLibraryNode={(nodeId) => setLibraryExpanded((current) => ({ ...current, [nodeId]: !current[nodeId] }))}
                  onExpandAllLibrary={(checked) => setLibraryExpanded(buildLibraryExpansionState(libraryTree, checked))}
                  libraryFilter={libraryFilter}
                  onLibraryFilterChange={setLibraryFilter}
                  activeDragNode={editDragNode}
                  onDragStart={(event) => event.active.data?.current?.type === 'libraryItem' && setEditDragNode(event.active.data.current.node)}
                  onDragCancel={() => setEditDragNode(null)}
                  onDragEnd={handleDragEndFactory('edit')}
                  searchPanel={{ targetMealOptions: editTargetMealOptions, targetMealId: editTargetMealId, onTargetMealChange: setEditTargetMealId, searchTerm: nutritionSearch, onSearchTermChange: (value) => { setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION }); setNutritionSearch(value) }, loading: nutritionLoading, rows: visibleNutritionRows, totalCount: nutritionTotalCount, pageIndex: nutritionPagination.pageIndex, pageSize: nutritionPagination.pageSize, onPageChange: (pageIndex) => setNutritionPagination((current) => ({ ...current, pageIndex })), expandedRowId: expandedNutritionResultId, onToggleRow: handleToggleNutritionResult, detailMap: nutritionDetailMap, detailLoadingId: nutritionDetailLoadingId, onAddNutritionFood: handleAddNutritionFoodToEdit }}
                  onReset={() => {
                    setEditFormState(editBaseline)
                    setActiveEditPlanId(editBaseline?.mealplans?.[0]?.id || null)
                    setEditTargetMealId(editBaseline?.mealplans?.[0]?.meals?.[0]?.id || '')
                    closeEditWorkspace()
                  }}
                  onSave={handleSaveEdit}
                  saveLabel="Save Nutrition Program"
                  saveLoading={editSaving}
                />
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'categories' ? (
        <Card className="border-slate-200 shadow-sm"><CardHeader><div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"><div><CardTitle className="text-lg">Nutrition Program Categories</CardTitle><CardDescription>Keep category hierarchy clean and review which nutrition programs live in each category.</CardDescription></div><div className="flex items-center gap-3"><span className="text-sm font-medium text-slate-600">View category layout</span><select className="flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none" value={categoryFilter} onChange={async (event) => { const value = event.target.value; setCategoryFilter(value); await loadCategoryRows(value) }}><option value="all">All Categories</option>{categoryFilterOptions.map((category) => <option key={`filter-${category.id}`} value={category.id}>{category.name}</option>)}</select></div></div></CardHeader><CardContent>{loadingCategoryRows ? <EmptyState message="Loading nutrition program categories..." /> : categoryRows.length ? <div className="overflow-x-auto rounded-lg border border-slate-200"><table className="min-w-full divide-y divide-slate-200 text-sm"><thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Parent Category</th><th className="px-5 py-3">Nutrition Programs</th><th className="px-5 py-3 text-right">Delete</th></tr></thead><tbody className="divide-y divide-slate-100 bg-white">{categoryRows.map((row) => <tr key={row.id}><td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td><td className="px-5 py-4 align-top"><select className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none" value={row.parent_id || ''} onChange={(event) => handleCategoryParentChange(row.id, event.target.value)} disabled={updatingCategoryId === row.id}><option value="">No Parent Category</option>{topLevelCategories.filter((candidate) => candidate.id !== row.id).map((candidate) => <option key={`${row.id}-${candidate.id}`} value={candidate.id}>{candidate.name}</option>)}</select></td><td className="px-5 py-4 align-top">{row.nutritionprograms?.length ? <div className="flex max-h-[116px] min-h-[116px] flex-wrap content-start gap-2 overflow-y-auto pr-2">{row.nutritionprograms.map((program) => <Badge key={program.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{program.name}</Badge>)}</div> : <div className="flex min-h-[116px] items-start py-1"><span className="text-slate-500">No nutrition programs assigned</span></div>}</td><td className="px-5 py-4 text-right align-top"><Button type="button" variant="ghost" size="icon" onClick={() => toast({ title: 'Delete endpoint not mapped', description: 'The category delete endpoint was not provided, so this view keeps delete as a UI placeholder.' })}><Trash2 className="h-4 w-4 text-slate-500" /></Button></td></tr>)}</tbody></table></div> : <EmptyState message="No nutrition program categories matched the current filter." />}</CardContent></Card>
      ) : null}
      </div>

      <Dialog open={!!noteDialog} onOpenChange={(open) => (!open ? setNoteDialog(null) : null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Meal Item Note</DialogTitle></DialogHeader>
          <Textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Add a note for this food item" className="min-h-[180px]" />
          <DialogFooter className="gap-2 sm:justify-end"><Button type="button" variant="outline" onClick={() => setNoteDialog(null)}>Cancel</Button><Button type="button" onClick={handleSaveNote}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteCatalogDialogOpen} onOpenChange={setDeleteCatalogDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete selected nutrition programs?</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-6 text-slate-500">
            This will permanently delete {selectedProgramIds.length} nutrition program{selectedProgramIds.length === 1 ? '' : 's'}.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteCatalogDialogOpen(false)} disabled={deletingCatalogRows}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteSelectedPrograms} disabled={deletingCatalogRows}>
              {deletingCatalogRows ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default NutritionProgramsWorkspace
